export interface OpenWorkerDetailPanelInput {
  workerAccountId: string
  viewerAccountId?: string
  tabKey?: string
}

export interface OpenPartnerWorkerDetailPanelInput {
  workerId: string
  employmentId?: string
}

export interface OpenContractDetailPanelInput {
  hireId: string
}

export interface OpenRecruiterDetailPanelInput {
  companyRecruiterId: string
}

export interface OpenClientDetailPanelInput {
  clientRelationId: string
}

type Listener<T> = (input: T) => void

/** A listener or an event that names no table: page-level hosts, and anything outside a table. */
const UNSCOPED = ''

/** Both sides name their table; otherwise every mounted table answers every click. */
class PanelChannel<T> {
  private readonly entries: { scope: string; listener: Listener<T> }[] = []

  add(listener: Listener<T>, scope: string = UNSCOPED): void {
    this.entries.push({ scope, listener })
  }

  remove(listener: Listener<T>): void {
    const index = this.entries.findIndex((entry) => entry.listener === listener)

    if (index !== -1) this.entries.splice(index, 1)
  }

  open(input: T, scope: string = UNSCOPED): void {
    for (const entry of this.entries) {
      const forThisTable = entry.scope === scope
      const eitherSideIsUnscoped = scope === UNSCOPED || entry.scope === UNSCOPED

      if (forThisTable || eitherSideIsUnscoped) entry.listener(input)
    }
  }
}

const workerChannel = new PanelChannel<OpenWorkerDetailPanelInput>()
const partnerWorkerChannel = new PanelChannel<OpenPartnerWorkerDetailPanelInput>()
const contractChannel = new PanelChannel<OpenContractDetailPanelInput>()
const recruiterChannel = new PanelChannel<OpenRecruiterDetailPanelInput>()
const clientChannel = new PanelChannel<OpenClientDetailPanelInput>()

export function addOpenWorkerDetailPanelListener(
  listener: Listener<OpenWorkerDetailPanelInput>,
  scope?: string,
) {
  workerChannel.add(listener, scope)
}

export function removeOpenWorkerDetailPanelListener(
  listener: Listener<OpenWorkerDetailPanelInput>,
) {
  workerChannel.remove(listener)
}

export function openWorkerDetailPanel(input: OpenWorkerDetailPanelInput, scope?: string) {
  workerChannel.open(input, scope)
}

export function addOpenPartnerWorkerDetailPanelListener(
  listener: Listener<OpenPartnerWorkerDetailPanelInput>,
  scope?: string,
) {
  partnerWorkerChannel.add(listener, scope)
}

export function removeOpenPartnerWorkerDetailPanelListener(
  listener: Listener<OpenPartnerWorkerDetailPanelInput>,
) {
  partnerWorkerChannel.remove(listener)
}

export function openPartnerWorkerDetailPanel(
  input: OpenPartnerWorkerDetailPanelInput,
  scope?: string,
) {
  partnerWorkerChannel.open(input, scope)
}

export function addOpenContractDetailPanelListener(
  listener: Listener<OpenContractDetailPanelInput>,
  scope?: string,
) {
  contractChannel.add(listener, scope)
}

export function removeOpenContractDetailPanelListener(
  listener: Listener<OpenContractDetailPanelInput>,
) {
  contractChannel.remove(listener)
}

export function openContractDetailPanel(input: OpenContractDetailPanelInput, scope?: string) {
  contractChannel.open(input, scope)
}

export function addOpenRecruiterDetailPanelListener(
  listener: Listener<OpenRecruiterDetailPanelInput>,
  scope?: string,
) {
  recruiterChannel.add(listener, scope)
}

export function removeOpenRecruiterDetailPanelListener(
  listener: Listener<OpenRecruiterDetailPanelInput>,
) {
  recruiterChannel.remove(listener)
}

export function openRecruiterDetailPanel(input: OpenRecruiterDetailPanelInput, scope?: string) {
  recruiterChannel.open(input, scope)
}

export function addOpenClientDetailPanelListener(
  listener: Listener<OpenClientDetailPanelInput>,
  scope?: string,
) {
  clientChannel.add(listener, scope)
}

export function removeOpenClientDetailPanelListener(
  listener: Listener<OpenClientDetailPanelInput>,
) {
  clientChannel.remove(listener)
}

export function openClientDetailPanel(input: OpenClientDetailPanelInput, scope?: string) {
  clientChannel.open(input, scope)
}

